import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { COMING_SOON } from "@/labConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Everyone who signs up anywhere in the lab lands on this Systeme segment.
export const LAB_SEGMENT = "Jewish Music Lab";

/** Inline name + email capture. Used in the hero, under each demo and in the footer. */
export function LabNotifyForm({ compact = false, testPrefix = "notify", onDone = null, source = "jewish_music_lab", extraTags = [], cta = "Notify me", doneText = "✓ You're in. We'll holler when the next experiment is ready to play with." }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/subscribe`, {
        email: email.trim(),
        first_name: firstName.trim(),
        source,
        segment: LAB_SEGMENT,
        extra_tags: extraTags,
      });
      setDone(true);
      toast.success("You're on the list — new experiments coming your way.");
      if (onDone) onDone();
    } catch (err) {
      toast.error("That didn't go through. Try again?");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <p
        className="text-sm"
        style={{ color: "#1B6C7A", fontFamily: "'Courier Prime', monospace" }}
        data-testid={`${testPrefix}-done`}
      >
        {doneText}
      </p>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`flex flex-col sm:flex-row gap-2 ${compact ? "" : "max-w-xl"}`}
      data-testid={`${testPrefix}-form`}
    >
      <Input
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        placeholder="First name"
        data-testid={`${testPrefix}-name`}
        className="h-11 bg-white border-[#D6CBB8] text-[#1A1523] placeholder:text-[#8C8198] sm:max-w-[9.5rem]"
      />
      <Input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        data-testid={`${testPrefix}-email`}
        className="h-11 flex-1 bg-white border-[#D6CBB8] text-[#1A1523] placeholder:text-[#8C8198]"
      />
      <Button
        type="submit"
        disabled={busy}
        data-testid={`${testPrefix}-submit`}
        className="h-11 rounded-full px-6 bg-[#D99B26] hover:bg-[#C2891D] text-white"
        style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.14em" }}
      >
        {busy ? "Sending…" : cta}
      </Button>
    </form>
  );
}

const PLAY_KEY = "jml_plays";
const ASK_KEY = "jml_notify_asked";

/** Counts how many times someone has actually played something in the lab. */
export function useLabPlays() {
  const [plays, setPlays] = useState(() => Number(localStorage.getItem(PLAY_KEY) || 0));
  const [askNotify, setAskNotify] = useState(false);
  const firedRef = useRef(false);

  const countPlay = () => {
    const next = Number(localStorage.getItem(PLAY_KEY) || 0) + 1;
    localStorage.setItem(PLAY_KEY, String(next));
    setPlays(next);
    // Second play onwards, once ever: ask if they'd like to hear about new toys.
    if (next >= 2 && !firedRef.current && !localStorage.getItem(ASK_KEY)) {
      firedRef.current = true;
      setTimeout(() => setAskNotify(true), 1200);
    }
  };

  const dismissNotify = () => {
    localStorage.setItem(ASK_KEY, "1");
    setAskNotify(false);
  };

  return { plays, countPlay, askNotify, dismissNotify };
}

/** The "enjoying the lab?" interstitial, shown on a repeat play. */
export function NotifyDialog({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
      style={{ backgroundColor: "rgba(26,21,35,0.45)" }}
      data-testid="repeat-visit-dialog"
    >
      <div
        className="w-full max-w-lg rounded-2xl p-6 sm:p-8 shadow-xl"
        style={{ backgroundColor: "#FFFBF2", border: "1px solid #EBE3D5" }}
      >
        <p
          className="text-xs uppercase mb-2"
          style={{ color: "#8C8198", fontFamily: "'Courier Prime', monospace", letterSpacing: "0.2em" }}
        >
          Still playing?
        </p>
        <h2
          className="text-2xl sm:text-3xl mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1523" }}
        >
          Enjoying the Jewish Music Lab?
        </h2>
        <p className="text-sm mb-5" style={{ color: "#5C5268", lineHeight: 1.7 }}>
          Leave your name and email and we'll let you know the moment there's a new
          experiment to play with — plus the occasional note from the lab.
        </p>
        <LabNotifyForm testPrefix="dialog-notify" onDone={onClose} />
        <button
          type="button"
          onClick={onClose}
          data-testid="repeat-visit-dismiss"
          className="mt-4 text-xs underline underline-offset-4"
          style={{ color: "#8C8198", fontFamily: "'Courier Prime', monospace" }}
        >
          No thanks, back to playing
        </button>
      </div>
    </div>
  );
}

// Floating Hebrew letters and music notes, kept from the Dan Berry site but
// re-tinted for a light room.
const GLYPHS = "אבגדהוזחטיכלמנסעפצקרשת♪♫♬♩𝄞".split("");
export function LabBackdrop() {
  const bits = React.useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => ({
        ch: GLYPHS[i % GLYPHS.length],
        left: (i * 97) % 100,
        top: (i * 53) % 100,
        size: 26 + ((i * 13) % 46),
        dur: 18 + ((i * 7) % 16),
        delay: -(i * 3) % 18,
        note: GLYPHS[i % GLYPHS.length].charCodeAt(0) > 0x2000,
      })),
    []
  );
  return (
    <div aria-hidden="true" className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 600px at 15% 0%, #FEF3D6 0%, rgba(254,243,214,0) 65%), radial-gradient(700px 500px at 85% 20%, #E3F3F5 0%, rgba(227,243,245,0) 70%), #FAF8F5",
        }}
      />
      {bits.map((b, i) => (
        <span
          key={i}
          className="absolute lab-float"
          style={{
            left: `${b.left}%`,
            top: `${b.top}%`,
            fontSize: b.size,
            color: b.note ? "rgba(217,155,38,0.22)" : "rgba(100,70,150,0.14)",
            fontFamily: b.note ? "serif" : "'Frank Ruhl Libre', serif",
            animationDuration: `${b.dur}s`,
            animationDelay: `${b.delay}s`,
          }}
        >
          {b.ch}
        </span>
      ))}
    </div>
  );
}

// Pre-launch the demos and about page redirect home, so they drop out of the
// chrome too — /support is the only lab page still standing on its own.
const NAV = COMING_SOON
  ? []
  : [
      { to: "/harp", label: "David's Harp", testId: "nav-link-harp" },
      { to: "/alephonics", label: "Alephonics", testId: "nav-link-hebrew" },
      { to: "/about", label: "About", testId: "nav-link-about" },
    ];

/** Light, playful chrome for every public lab page. */
export default function LabShell({ children, wide = false }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [pathname]);

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: "#FAF8F5", color: "#1A1523", fontFamily: "'Nunito', ui-sans-serif, system-ui, sans-serif" }}>
      <LabBackdrop />
      <header
        className="sticky top-0 z-50 border-b"
        style={{ backgroundColor: "#FAF8F5", borderColor: "#EBE3D5" }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="nav-brand-logo">
            <img
              src="/lab-logo.png"
              alt=""
              aria-hidden="true"
              className="shrink-0"
              style={{ width: 38, height: 38, objectFit: "contain" }}
            />
            <span
              className="truncate text-lg sm:text-xl"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              Jewish Music <span style={{ color: "#D99B26" }}>Lab</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-sm">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                className="transition-colors hover:text-[#E86F4A]"
                style={{
                  color: pathname === n.to ? "#E86F4A" : "#5C5268",
                  fontFamily: "'Courier Prime', monospace",
                  letterSpacing: "0.08em",
                }}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/support"
              data-testid="nav-link-support"
              className="rounded-full px-4 py-2 text-xs transition-colors"
              style={{
                backgroundColor: "#E86F4A",
                color: "#FFFFFF",
                fontFamily: "'Courier Prime', monospace",
                letterSpacing: "0.14em",
              }}
            >
              SUPPORT THE LAB
            </Link>
          </nav>

          <button
            type="button"
            className="md:hidden text-sm px-3 py-2 rounded-full"
            style={{ backgroundColor: "#FEF3D6", color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t px-5 py-3 flex flex-col gap-3" style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}>
            {[...NAV, { to: "/support", label: "Support the Lab", testId: "nav-mobile-support" }].map((n) => (
              <Link key={n.to} to={n.to} data-testid={`m-${n.testId}`} style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}>
                {n.label}
              </Link>
            ))}
          </div>
        )}
      </header>

      <main className={`relative z-10 mx-auto px-5 sm:px-6 ${wide ? "max-w-[1200px]" : "max-w-6xl"}`}>
        {children}
      </main>

      <footer className="relative z-10 border-t mt-20" style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-12 grid gap-10 md:grid-cols-[1.2fr_1fr]">
          <div>
            <h3 className="text-xl sm:text-2xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Get the next experiment
            </h3>
            <p className="text-sm mb-4" style={{ color: "#5C5268" }}>
              New toys land here every so often. Leave your email and you'll be first to play.
            </p>
            <LabNotifyForm testPrefix="footer-notify" />
          </div>
          <div className="text-sm" style={{ color: "#5C5268" }}>
            <img
              src="/lab-logo.png"
              alt="Jewish Music Lab"
              className="mb-4"
              style={{ width: 52, height: 52, objectFit: "contain" }}
              data-testid="footer-brand-logo"
            />
            <p className="text-xs uppercase mb-3" style={{ color: "#8C8198", fontFamily: "'Courier Prime', monospace", letterSpacing: "0.2em" }}>
              The Lab
            </p>
            <ul className="space-y-2">
              {!COMING_SOON && (
                <>
                  <li><Link to="/harp" className="hover:text-[#E86F4A]">Play David's Harp</Link></li>
                  <li><Link to="/alephonics" className="hover:text-[#E86F4A]">Alephonics: Translate Hebrew Into Music</Link></li>
                  <li><Link to="/about" className="hover:text-[#E86F4A]">About the lab</Link></li>
                </>
              )}
              <li><Link to="/support" className="hover:text-[#E86F4A]">Support the Jewish Music Lab</Link></li>
              <li>
                <a
                  href="https://danberrymusic.com"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#E86F4A]"
                  data-testid="nav-link-danberrymusic"
                >
                  danberrymusic.com ↗
                </a>
              </li>
            </ul>
            <p className="mt-6 text-xs" style={{ color: "#8C8198" }}>
              Built by Dan Berry · <Link to="/privacy" className="underline underline-offset-2">Privacy</Link> ·{" "}
              <Link to="/terms" className="underline underline-offset-2">Terms</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
